import type { DocFieldSpec, DocumentSource, LayoutBlock, LayoutBlockType, LayoutFieldRow, LayoutPage, TemplateLayout } from "@wms/domain";

/**
 * KZ-DOC UI02 (TMP14): the editor of a declarative layout works on the same blocks the server accepts
 * (`printing/layout.py`, extra=forbid): the helpers here only build, move and describe them — no code, no markup.
 */
export const BLOCK_LABELS: Record<LayoutBlockType, string> = {
  title: "Заголовок",
  text: "Текст",
  fields: "Реквизиты",
  parties: "Стороны",
  table: "Таблица",
  totals: "Итоги",
  signatures: "Подписи",
  barcode: "Штрихкод",
  spacer: "Отступ",
  page_break: "Новая страница",
};

export const BLOCK_ORDER: LayoutBlockType[] = ["title", "text", "fields", "parties", "table", "totals", "signatures", "barcode", "spacer", "page_break"];

export const FORMAT_OPTIONS = [
  { value: "text", label: "Текст" },
  { value: "qty", label: "Количество" },
  { value: "money", label: "Сумма" },
  { value: "date", label: "Дата" },
  { value: "datetime", label: "Дата и время" },
  { value: "int", label: "Целое число" },
  { value: "bool", label: "Да / нет" },
];

export const DEFAULT_PAGE: Required<Omit<LayoutPage, "footer">> & { footer: string | null } = {
  size: "A4",
  orientation: "portrait",
  margins_mm: [14, 12, 16, 14],
  font_size: 9,
  page_numbers: true,
  footer: null,
};

/** The first field of the kind among the preferred paths, else the first field at all. */
export function firstField(source: DocumentSource | null | undefined, prefer: string[] = []): string {
  const paths = new Set((source?.fields ?? []).map((f) => f.path));
  for (const p of prefer) if (paths.has(p)) return p;
  return source?.fields[0]?.path ?? "document.number";
}

export function tableNames(source: DocumentSource | null | undefined): string[] {
  return Object.keys(source?.tables ?? {});
}

export function tableColumns(source: DocumentSource | null | undefined, table: string): DocFieldSpec[] {
  return source?.tables?.[table] ?? [];
}

/** Why a block cannot be added to this kind (a table needs a table of lines), or null. */
export function cannotAdd(type: LayoutBlockType, source: DocumentSource | null | undefined): string | null {
  if (!source) return "Сначала загрузится список полей вида документа";
  if (type === "table" && !tableNames(source).length) return "У этого вида документа нет таблиц строк";
  if (!source.fields.length && type !== "spacer" && type !== "page_break" && type !== "text") return "У вида документа нет полей";
  return null;
}

/** A new block with valid starting values: it passes the server check as soon as it is added. */
export function defaultBlock(type: LayoutBlockType, source: DocumentSource | null | undefined): LayoutBlock {
  const row = (label: string, prefer: string[], bold = false): LayoutFieldRow => ({ label, field: firstField(source, prefer), ...(bold ? { bold } : {}) });
  switch (type) {
    case "title":
      return { type: "title", text: "Название документа", number: true, align: "center" };
    case "text":
      return { type: "text", text: "Текст", style: "normal" };
    case "fields":
      return { type: "fields", title: "Реквизиты", columns: 2, rows: [row("Номер", ["document.number"]), row("Дата", ["document.date"])], hide_empty: true };
    case "parties":
      return {
        type: "parties",
        columns: [{ title: "Наша организация", rows: [row("Наименование", ["org.name"], true), row("БИН/ИИН", ["org.tax_id"])] }],
      };
    case "table": {
      const name = tableNames(source)[0] ?? "lines";
      const columns = tableColumns(source, name).slice(0, 4);
      return {
        type: "table",
        source: name,
        columns: columns.length ? columns.map((c) => ({ title: c.label, field: c.path, width: 10 })) : [{ title: "Наименование", field: "name", width: 20 }],
        totals: [],
        total_label: "Итого",
        empty_text: "Строк нет",
        numbered: false,
      };
    }
    case "totals":
      return { type: "totals", rows: [row("Итого", (source?.fields ?? []).filter((f) => f.type === "money" || f.type === "qty").map((f) => f.path), true)] };
    case "signatures":
      return { type: "signatures", items: [{ role: "Сдал" }, { role: "Принял" }], columns: 2, stamp: false };
    case "barcode":
      return { type: "barcode", field: firstField(source, ["document.number"]), kind: "code128", height_mm: 12 };
    case "spacer":
      return { type: "spacer", height_mm: 4 };
    case "page_break":
      return { type: "page_break" };
  }
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const out = list.slice();
  const [item] = out.splice(from, 1);
  out.splice(to, 0, item);
  return out;
}

export function removeAt<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}

export function replaceAt<T>(list: T[], index: number, item: T): T[] {
  return list.map((x, i) => (i === index ? item : x));
}

/** The layout as the editor holds it: page settings and the list of required requisites are always present. */
export function normalizeLayout(raw: unknown): TemplateLayout {
  const value = (raw && typeof raw === "object" ? raw : {}) as Partial<TemplateLayout>;
  return {
    page: { ...DEFAULT_PAGE, ...(value.page ?? {}) },
    blocks: Array.isArray(value.blocks) ? value.blocks : [],
    required: Array.isArray(value.required) ? value.required : [],
  };
}

/** A short line about a block for its card and for a read-only version: what it prints. */
export function blockSummary(block: LayoutBlock, source?: DocumentSource | null): string {
  const label = (path: string) => source?.fields.find((f) => f.path === path)?.label ?? path;
  switch (block.type) {
    case "title":
      return `«${block.text}»${block.number === false ? "" : ", номер и дата документа"}`;
    case "text":
      return block.text.length > 60 ? `${block.text.slice(0, 60)}…` : block.text;
    case "fields":
    case "totals":
      return `${block.rows.length} ${rowsWord(block.rows.length)}: ${block.rows.slice(0, 3).map((r) => r.label).join(", ")}${block.rows.length > 3 ? "…" : ""}`;
    case "parties":
      return block.columns.map((c) => c.title).join(" · ");
    case "table":
      return `строки «${block.source}», колонок: ${block.columns.length}${block.totals?.length ? `, итогов: ${block.totals.length}` : ""}`;
    case "signatures":
      return block.items.map((i) => i.role).join(", ");
    case "barcode":
      return `${block.kind === "qr" ? "QR-код" : "Code 128"}: ${label(block.field)}`;
    case "spacer":
      return `${block.height_mm ?? 4} мм`;
    case "page_break":
      return "следующий блок начнётся с новой страницы";
  }
}

function rowsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "строка";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "строки";
  return "строк";
}

/** Options of a field picker: the Russian name first, the path for the administrator in small print. */
export function fieldOptions(fields: DocFieldSpec[]): { value: string; label: string; search: string }[] {
  return fields.map((f) => ({ value: f.path, label: `${f.label} · ${f.path}`, search: `${f.label} ${f.path}`.toLowerCase() }));
}

/** Appends `{path}` to a text: the placeholder the generator fills from the snapshot. */
export function withPlaceholder(text: string | null | undefined, path: string): string {
  const base = text ?? "";
  return `${base}${base && !base.endsWith(" ") ? " " : ""}{${path}}`;
}
